import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, DollarSign, Star, Heart, User, Image, CalendarDays, BarChart3, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import api from '@/services/api';
import { toast } from 'sonner';
import { SlideToConfirm } from '@/components/SlideToConfirm';
import GoldenScissors from '@/components/GoldenScissors';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { errorDetail } from '@/utils/apiError';

export default function StylistDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Everything beyond profile-editing stays dark until the required steps are done
  // (3 docs + 3 photos + ABN + social) — Edit Profile IS the guided next step.
  const [stepsComplete, setStepsComplete] = useState(false);
  const [completingBooking, setCompletingBooking] = useState(null); // booking being completed
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [submittingComplete, setSubmittingComplete] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    // Gate: an incomplete stylist must finish onboarding before using the dashboard.
    api.get('/stylists/onboarding-status')
      .then(({ data }) => { if (!data.complete) navigate('/onboarding'); })
      .catch(() => {});
    api.get('/stylists/verification-progress')
      .then(({ data }) => setStepsComplete(!!data.requirements_met))
      .catch(() => {});
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [bookingsRes, profileRes] = await Promise.all([
        api.get('/bookings/my-bookings'),
        api.get('/stylists/profile/me')
      ]);
      setBookings(bookingsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const totalEarnings = bookings
    .filter(b => b.payment_status === 'completed')
    .reduce((sum, b) => sum + (b.total_price || 0), 0);

  const acceptBooking = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { new_status: 'confirmed' });
      toast.success('Booking accepted — countdown started');
      fetchData();
    } catch (e) {
      toast.error(errorDetail(e, 'Could not accept the booking'));
    }
  };

  const submitCompletion = async () => {
    if (!afterFile) { toast.error('The After photo is required — that\'s your proof of work'); return; }
    setSubmittingComplete(true);
    try {
      const fd = new FormData();
      fd.append('photo_after', afterFile);
      if (beforeFile) fd.append('photo_before', beforeFile);
      await api.post(`/bookings/${completingBooking.booking_id}/complete`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Job completed — photos are live on your Style Book carousel ✂️');
      setCompletingBooking(null);
      setBeforeFile(null);
      setAfterFile(null);
      fetchData();
    } catch (e) {
      toast.error(errorDetail(e, 'Could not complete the booking'));
    } finally {
      setSubmittingComplete(false);
    }
  };

  const stats = [
    {
      title: 'Total Bookings',
      value: profile?.total_bookings || 0,
      icon: <Calendar className="h-8 w-8 text-secondary" />,
    },
    {
      title: 'Hearts',
      subtitle: 'Your Onda reputation',
      value: profile?.hearts || 3, // everyone starts at 3 hearts
      icon: <Heart className="h-8 w-8 text-secondary fill-secondary" />,
    },
    {
      title: 'Earnings',
      value: `$${totalEarnings.toFixed(2)}`,
      icon: <DollarSign className="h-8 w-8 text-secondary" />,
    },
    {
      title: 'Rating',
      subtitle: 'Star average from clients',
      value: profile?.average_rating?.toFixed(1) || '0.0',
      icon: <Star className="h-8 w-8 text-secondary fill-secondary" />,
    },
  ];

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Centred header — same layout the mobile app will use (future-proofing). */}
        <div className="mb-12 text-center" data-testid="stylist-dashboard-header">
          <h1 className="font-playfair text-5xl font-bold text-primary mb-2">Welcome, {user?.first_name || user?.name}</h1>
          <p className="text-lg text-muted-foreground">Manage your bookings and grow your business</p>
          {profile?.hearts >= 1000 && (
            <Badge className="mt-4 bg-secondary text-white px-4 py-2 text-lg" data-testid="executive-tier-badge">
              🏆 Executive Tier Stylist
            </Badge>
          )}
          {/* One uniform action row — identical shape and width, locked ones go dark. */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Button
              onClick={() => navigate('/stylist/profile')}
              className="btn-primary flex items-center justify-center gap-2 min-w-[210px]"
            >
              <Image className="h-4 w-4" />
              Edit Profile & Style Book
            </Button>
            <Button
              onClick={() => navigate('/stylist/analytics')}
              variant="outline"
              className={`flex items-center justify-center gap-2 min-w-[210px] border-primary/30 ${!stepsComplete ? 'opacity-40' : ''}`}
              disabled={!stepsComplete}
              title={!stepsComplete ? 'Unlocks once your profile setup is complete' : undefined}
            >
              {stepsComplete ? <BarChart3 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              Analytics
            </Button>
            <Button
              onClick={() => navigate('/stylist/calendar')}
              variant="outline"
              className={`flex items-center justify-center gap-2 min-w-[210px] border-primary/30 ${!stepsComplete ? 'opacity-40' : ''}`}
              disabled={!stepsComplete}
              title={!stepsComplete ? 'Unlocks once your profile setup is complete' : undefined}
            >
              {stepsComplete ? <CalendarDays className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              Calendar & Availability
            </Button>
            <Button
              variant="outline"
              className="flex items-center justify-center gap-2 min-w-[210px] border-primary/30 opacity-40"
              disabled
              title="Coming soon — auto-posting managed by Onda (Premium)"
            >
              <Lock className="h-4 w-4" />
              Social Media
            </Button>
            <Button
              variant="outline"
              className="flex items-center justify-center gap-2 min-w-[210px] border-primary/30 opacity-40"
              disabled
              title="Coming soon — your curated WebShop"
            >
              <Lock className="h-4 w-4" />
              Shop
            </Button>
          </div>
          {!stepsComplete && (
            <p className="text-xs text-muted-foreground mt-3">
              Finish your profile — credentials, photos, ABN and a social — to unlock the rest.
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
          {stats.map((stat, idx) => (
            <Card key={idx} className="border border-border/40">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold font-playfair">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-xs text-muted-foreground/70 mt-1">{stat.subtitle}</p>
                    )}
                  </div>
                  {stat.icon}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border border-border/40" data-testid="bookings-card">
          <CardHeader>
            <CardTitle className="font-playfair text-2xl">Your Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading bookings...</p>
            ) : bookings.length > 0 ? (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <div key={booking.booking_id} className="flex items-center justify-between p-4 border border-border/40 rounded-sm" data-testid={`booking-item-${booking.booking_id}`}>
                    <div>
                      <p className="font-semibold">{booking.client_name}</p>
                      <p className="text-sm text-muted-foreground">{new Date(booking.scheduled_datetime).toLocaleString()}</p>
                      <div className="flex gap-2 mt-2">
                        {booking.services?.map((service, idx) => (
                          <Badge key={idx} variant="outline" className="capitalize">
                            {service.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-right space-y-2">
                      <p className="text-lg font-bold">${booking.total_price?.toFixed(2)}</p>
                      <Badge
                        className={`${
                          booking.status === 'completed' ? 'bg-green-500' :
                          booking.status === 'confirmed' ? 'bg-primary text-black' :
                          booking.status === 'cancelled' ? 'bg-red-500' :
                          'bg-yellow-500'
                        }`}
                        data-testid={`booking-status-${booking.booking_id}`}
                      >
                        {booking.status === 'confirmed' ? 'pending completion' : booking.status}
                      </Badge>
                      {booking.status === 'pending' && (
                        <div className="w-64">
                          <SlideToConfirm text="Slide to accept ✂️" onConfirm={() => acceptBooking(booking.booking_id)} />
                        </div>
                      )}
                      {booking.status === 'confirmed' && (
                        <Button size="sm" className="btn-primary"
                          onClick={() => setCompletingBooking(booking)}>
                          Complete booking
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No bookings yet. Your clients will appear here.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!completingBooking} onOpenChange={(open) => { if (!open) setCompletingBooking(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl text-primary">Complete this job</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Upload your proof of work — the <strong>After</strong> photo is required and goes straight to
            your Style Book carousel. Your client gets the "Rate your new look!!" moment.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-sm mb-1 block">Before (optional)</Label>
              <label className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-border hover:border-primary/60 cursor-pointer text-sm">
                {beforeFile ? <span className="text-foreground font-medium">{beforeFile.name}</span> : 'Tap to choose the Before photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => setBeforeFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div>
              <Label className="text-sm mb-1 block">After — proof of work <span className="text-red-500">*</span></Label>
              <label className="flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-primary/40 hover:border-primary cursor-pointer text-sm">
                {afterFile ? <span className="text-foreground font-medium">{afterFile.name}</span> : 'Tap to choose the After photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => setAfterFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <Button className="btn-primary w-full" onClick={submitCompletion} disabled={submittingComplete}>
            {submittingComplete ? <GoldenScissors size="sm" className="mr-2" /> : null}
            Mark completed
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
